/**
 * SpacebarPanController Component
 * Toggle a follow-mouse pan mode with the spacebar (or a floating hand button)
 *
 * Pressing Space (or clicking the hand button) toggles a "navigation mode":
 * while active, moving the mouse pans the map directly (no click required).
 * Press Space / click the button again — or press Escape — to exit.
 *
 * This is trackpad-friendly because it doesn't require holding multiple
 * inputs simultaneously, and keeps the precision-editing default
 * (no accidental pan) when the mode is off.
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMap } from 'react-leaflet';
import { Hand } from 'lucide-react';

export function SpacebarPanController() {
  const map = useMap();
  const [panMode, setPanMode] = useState(false);
  const panModeRef = useRef(false);
  const lastMouseRef = useRef<{ x: number; y: number } | null>(null);

  // Keep ref in sync so window listeners read the latest value.
  useEffect(() => { panModeRef.current = panMode; }, [panMode]);

  // Default map dragging stays disabled — this controller drives pan via panBy.
  useEffect(() => {
    map.dragging.disable();
  }, [map]);

  // Cursor feedback on the map container while pan mode is active.
  useEffect(() => {
    const container = map.getContainer();
    if (panMode) {
      const prev = container.style.cursor;
      container.style.cursor = 'grab';
      return () => { container.style.cursor = prev; };
    }
  }, [map, panMode]);

  useEffect(() => {
    const isTyping = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isTyping(e)) return;
      if (e.code === 'Space') {
        e.preventDefault();
        // Activate on first press (ignore auto-repeats). Hold-to-pan.
        if (!e.repeat && !panModeRef.current) {
          setPanMode(true);
        }
      } else if (e.code === 'Escape' && panModeRef.current) {
        setPanMode(false);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space' && panModeRef.current) {
        e.preventDefault();
        setPanMode(false);
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      const x = e.clientX;
      const y = e.clientY;
      const prev = lastMouseRef.current;
      // Always keep the last known cursor position. When pan mode activates, the
      // first mousemove already has a reference to compute delta against — no
      // wasted frame waiting for a baseline.
      if (panModeRef.current && prev) {
        const dx = x - prev.x;
        const dy = y - prev.y;
        if (dx !== 0 || dy !== 0) {
          map.panBy([-dx, -dy], { animate: false });
        }
      }
      lastMouseRef.current = { x, y };
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [map]);

  const toggleButton = (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setPanMode(prev => !prev);
      }}
      title={panMode ? 'Modo navegación activo — clic para salir' : 'Activar modo navegación (o mantené Espacio)'}
      style={{
        position: 'absolute',
        top: 12,
        left: 12,
        zIndex: 1000,
        width: 36,
        height: 36,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 8,
        border: '1px solid ' + (panMode ? '#f59e0b' : '#d1d5db'),
        background: panMode ? '#fef3c7' : '#ffffff',
        color: panMode ? '#92400e' : '#374151',
        cursor: 'pointer',
        boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)',
        transition: 'background 120ms, border-color 120ms',
      }}
    >
      <Hand size={18} />
    </button>
  );

  return createPortal(toggleButton, map.getContainer());
}
